import { useState, useMemo } from "react";
import { Plus, Edit, Trash2, AlertCircle } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { usePriceTables, type PriceTable, type PriceTableInsert } from "@/features/pessoas/hooks/usePriceTables";

const emptyForm = (): PriceTableInsert => ({ descricao: "", ordemBusca: 1, isAtiva: true });

export default function PriceTables() {
  const {
    priceTables,
    isLoading,
    createPriceTable,
    updatePriceTable,
    deletePriceTable,
    isCreating,
    isUpdating,
    isDeleting,
  } = usePriceTables();

  const [dialogOpen, setDialogOpen]     = useState(false);
  const [deleteId, setDeleteId]         = useState<number | null>(null);
  const [editingTable, setEditingTable] = useState<PriceTable | null>(null);
  const [formData, setFormData]         = useState<PriceTableInsert>(emptyForm());
  const [busca, setBusca]               = useState("");

  // ── Ordens em uso (excluindo a tabela que está sendo editada) ──────────
  const ordensEmUso = useMemo(() =>
    new Set(
      (priceTables ?? [])
        .filter((t) => !editingTable || t.id !== editingTable.id)
        .map((t) => t.ordemBusca)
    ),
    [priceTables, editingTable]
  );

  const ordemDuplicada = ordensEmUso.has(formData.ordemBusca ?? 0);

  const proximaOrdemDisponivel = useMemo(() => {
    let next = 1;
    while (ordensEmUso.has(next)) next++;
    return next;
  }, [ordensEmUso]);

  // ── Abrir dialog ───────────────────────────────────────────────────────
  const openNew = () => {
    setEditingTable(null);
    // Pré-preenche com a próxima ordem disponível
    const ordens = new Set((priceTables ?? []).map((t) => t.ordemBusca));
    let next = 1;
    while (ordens.has(next)) next++;
    setFormData({ ...emptyForm(), ordemBusca: next });
    setDialogOpen(true);
  };

  const openEdit = (table: PriceTable) => {
    setEditingTable(table);
    setFormData({ descricao: table.descricao ?? "", ordemBusca: table.ordemBusca, isAtiva: table.isAtiva });
    setDialogOpen(true);
  };

  // ── Submeter ───────────────────────────────────────────────────────────
  const handleSubmit = () => {
    if (ordemDuplicada) return;
    if (editingTable) {
      updatePriceTable({ id: editingTable.id, data: formData });
    } else {
      createPriceTable(formData);
    }
    setDialogOpen(false);
  };

  const handleDelete = () => {
    if (deleteId !== null) {
      deletePriceTable(deleteId);
      setDeleteId(null);
    }
  };

  const tablesFiltradas = busca
    ? (priceTables ?? []).filter(t => t.descricao?.toLowerCase().includes(busca.toLowerCase()))
    : (priceTables ?? []);

  const paginacao = usePaginacaoLocal(tablesFiltradas);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tabelas de Preço</h1>
          <p className="text-muted-foreground">Gerencie as tabelas de preço dos produtos</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Tabela
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24 text-center">Ordem</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-20">Ativa</TableHead>
              <TableHead className="w-24">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : paginacao.itensPaginados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhuma tabela encontrada para esta busca." : "Nenhuma tabela cadastrada"}
                </TableCell>
              </TableRow>
            ) : (
              paginacao.itensPaginados.map((table) => (
                <TableRow key={table.id}>
                  <TableCell className="text-center font-mono font-medium">
                    {table.ordemBusca}
                  </TableCell>
                  <TableCell className="font-medium">{table.descricao || "-"}</TableCell>
                  <TableCell>{table.isAtiva ? "Sim" : "Não"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(table)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(table.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* ── Dialog criar/editar ──────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTable ? "Editar Tabela" : "Nova Tabela"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Ordem de busca */}
            <div className="space-y-2">
              <Label>
                Ordem de Busca*
                <span className="ml-1 text-xs text-muted-foreground font-normal">
                  (define a prioridade na consulta de preços)
                </span>
              </Label>
              <Input
                type="number"
                min={1}
                value={formData.ordemBusca ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, ordemBusca: parseInt(e.target.value) || 1 })
                }
                className={ordemDuplicada ? "border-destructive focus-visible:ring-destructive" : ""}
              />
              {ordemDuplicada && (
                <p className="flex items-center gap-1.5 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Ordem {formData.ordemBusca} já está em uso.{" "}
                  <button
                    type="button"
                    className="underline font-medium"
                    onClick={() =>
                      setFormData({ ...formData, ordemBusca: proximaOrdemDisponivel })
                    }
                  >
                    Usar {proximaOrdemDisponivel}
                  </button>
                </p>
              )}
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label>Descrição*</Label>
              <Input
                value={formData.descricao ?? ""}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </div>

            {/* Ativa */}
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isAtiva ?? true}
                onCheckedChange={(checked) => setFormData({ ...formData, isAtiva: checked })}
              />
              <Label>Ativa</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.descricao || ordemDuplicada || isCreating || isUpdating}
            >
              {isCreating || isUpdating ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog confirmar exclusão ────────────────────────────────────── */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta tabela de preço?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
